import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ArrendondaCasasDecimaisPipe } from 'app/pipes/arrendondaCasasDecimais/arrendonda-casas-decimais.pipe';

interface PontoMarcacao {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  labelX: number;
  labelY: number;
}

@Component({
  selector: 'app-velocimetro-mapa',
  standalone: true,
  imports: [CommonModule, ArrendondaCasasDecimaisPipe],
  templateUrl: './velocimetro-mapa.component.html',
  styleUrl: './velocimetro-mapa.component.css'
})
export class VelocimetroMapaComponent {
  @Input() velocidade: number = 0;
  @Input() velocidadeMinima: number = 0;
  @Input() velocidadeMaxima: number = 0;
  @Input() corAbaixo: string = '#FFEB3B';
  @Input() corIdeal: string = '#4CAF50';
  @Input() corAcima: string = '#F44336';
  @Input() dataUtc: string = '';
  @Input() horaUtc: string = '';

  private readonly CENTRO_X = 100;
  private readonly CENTRO_Y = 105;
  private readonly LARGURA_TRANSICAO = 6; // pontos percentuais de meia-largura da rampa suave no gradiente

  get escalaMaxima(): number {
    return Math.ceil(Math.max(this.velocidadeMaxima * 1.5, this.velocidade, 1));
  }

  get rotacaoPonteiro(): number {
    return this.anguloParaValor(this.velocidade);
  }

  get offsetMinimo(): number {
    return this.offsetParaValor(this.velocidadeMinima);
  }

  get offsetMaximo(): number {
    return this.offsetParaValor(this.velocidadeMaxima);
  }

  get offsetMinimoInicioTransicao(): number {
    return Math.max(0, this.offsetMinimo - this.LARGURA_TRANSICAO);
  }

  get offsetMinimoFimTransicao(): number {
    return Math.min(this.offsetMaximo, this.offsetMinimo + this.LARGURA_TRANSICAO);
  }

  get offsetMaximoInicioTransicao(): number {
    return Math.max(this.offsetMinimoFimTransicao, this.offsetMaximo - this.LARGURA_TRANSICAO);
  }

  get offsetMaximoFimTransicao(): number {
    return Math.min(100, this.offsetMaximo + this.LARGURA_TRANSICAO);
  }

  get marcacaoMinima(): PontoMarcacao {
    return this.pontosDaMarcacao(this.velocidadeMinima);
  }

  get marcacaoMaxima(): PontoMarcacao {
    return this.pontosDaMarcacao(this.velocidadeMaxima);
  }

  private anguloParaValor(valor: number): number {
    const rotacao = (valor / this.escalaMaxima) * 180 - 90;
    return Math.min(90, Math.max(-90, rotacao));
  }

  private pontoNoArco(valor: number, raio: number): { x: number; y: number } {
    const radianos = (this.anguloParaValor(valor) * Math.PI) / 180;
    return {
      x: this.CENTRO_X + raio * Math.sin(radianos),
      y: this.CENTRO_Y - raio * Math.cos(radianos)
    };
  }

  private offsetParaValor(valor: number): number {
    const radianos = (this.anguloParaValor(valor) * Math.PI) / 180;
    return (1 + Math.sin(radianos)) * 50;
  }

  private pontosDaMarcacao(valor: number): PontoMarcacao {
    const inicio = this.pontoNoArco(valor, 78);
    const fim = this.pontoNoArco(valor, 90);
    const label = this.pontoNoArco(valor, 92); // was 100 — fits inside the viewBox
    return { x1: inicio.x, y1: inicio.y, x2: fim.x, y2: fim.y, labelX: label.x, labelY: label.y };
  }
}
